/**
 * 設定 → 見積依頼（#119、docs/56 §6.8）。使う機能 {vendorQuote} の会社だけメニューに出る。書けるのは管理者だけ
 *   回答期限（送る日から n 日。1〜60）・条件の既定文・メールの件名と本文。差し込みは ［宛名］［番号］［件名］［回答期限］［自社名］［担当者］
 *   製品ごとの既定（contract の PRODUCTS[*].rfq）に戻せる。変えても作ってある下書きは変わらない
 *   自動で保存（S-00008 の続き、10/10 射場さん：設定の画面は自動で保存に揃える。前は下の「見積依頼の既定を保存」。lib/autoSave.tsx）：
 *     欄を離れたとき・Enter（件名・日数）・画面を離れるとき。題の右に保存の様子。回答期限が 1〜60 の外などは送らず下に朱の1行（そのまま離れようとしたら確かめる）。
 *     「製品既定に復元」はすぐ効く（今どおり）
 */
import { RFQ_MAIL_FIELDS, RFQ_MSG, RfqDefaults, SETTINGS_MSG, type RfqSettings } from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { api, messageOf } from "../api.js";
import { useMe } from "../auth.js";
import { BackButton, useBackNav, useLeaveGuard, usePageName } from "../components/BackButton.jsx";
import { NumInput } from "../components/NumInput.jsx";
import { ErrorText, Field, Help, useToast } from "../components/ui.jsx";
import { t } from "../labels.js";
import { AutoSaveStatus, useAutoSave } from "../lib/autoSave.jsx";

const KEY = ["company-rfq-settings"] as const;
type F = { replyDays: number | null; terms: string; mailSubject: string; mailBody: string };
const fOf = (s: RfqSettings): F => ({ replyDays: s.replyDays, terms: s.terms, mailSubject: s.mailSubject, mailBody: s.mailBody });

export function SettingsRfqPage() {
  const qc = useQueryClient();
  const me = useMe();
  const admin = me.data?.user.role === "admin";
  const { toast, node } = useToast();
  const q = useQuery({ queryKey: KEY, queryFn: () => api<RfqSettings>("GET", "/company/rfq-settings") });
  const last = useRef<"mailSubject" | "mailBody" | "terms">("mailBody");
  const a = useAutoSave<F>({
    server: q.data ? fOf(q.data) : null,
    enabled: admin,
    send: async (v) => {
      const r = await api<RfqSettings>("PUT", "/company/rfq-settings", RfqDefaults.parse({ ...v, replyDays: v.replyDays ?? null }));
      qc.setQueryData(KEY, r);
      return fOf(r);
    },
    check: (v) => {
      const r = RfqDefaults.safeParse({ ...v, replyDays: v.replyDays ?? null });
      if (r.success) return null;
      const i = r.error.issues[0];
      return { status: SETTINGS_MSG.autoSaveFailed, errors: { [String(i?.path[0] ?? "rfq")]: i?.path[0] === "replyDays" ? RFQ_MSG.replyDaysRange : (i?.message ?? SETTINGS_MSG.autoSaveFailed) } };
    },
    hide: (v) => ({ url: "/api/v1/company/rfq-settings", method: "PUT", body: { ...v, replyDays: v.replyDays ?? null } }),
  });
  const f = a.form;
  const err = a.serverError ?? Object.values(a.errors)[0];
  const back = useBackNav({ to: "/settings", label: "設定" }, a.unsent);
  const guard = useLeaveGuard(a.unsent);
  usePageName(RFQ_MSG.settingsTitle);
  /** 製品の既定に戻す：すぐ効く（自動で保存とは別） */
  const reset = useMutation({
    mutationFn: () => api<RfqSettings>("PUT", "/company/rfq-settings", null),
    onSuccess: (v) => {
      qc.setQueryData(KEY, v);
      a.replace(fOf(v));
      toast(RFQ_MSG.settingsResetDone);
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });
  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!q.data || !f) return <p className="hint">読み込んでいます…</p>;
  const put = (p: Partial<F>) => a.set(p);
  const insert = (field: string) => {
    const k = last.current;
    const el = document.getElementById(`rqs_${k}`) as HTMLInputElement | HTMLTextAreaElement | null;
    const cur = f[k];
    const at = el && el.selectionStart != null ? el.selectionStart : cur.length;
    put({ [k]: cur.slice(0, at) + field + cur.slice(at) } as Partial<F>);
    window.setTimeout(() => {
      el?.focus();
      el?.setSelectionRange(at + field.length, at + field.length);
    }, 0);
  };
  return (
    <>
      <BackButton back={back} />
      <div className="hrow">
        <h1>{RFQ_MSG.settingsTitle}</h1>
        <Help text={t(RFQ_MSG.settingsHelp)} testId="help-rfq-settings" />
        {q.data.source === "product" ? <span className="chip" data-testid="rfq-settings-product">{RFQ_MSG.settingsIsDefault}</span> : null}
        <span className="sp" />
        {admin ? <AutoSaveStatus state={a.state} testId="rfq-autosave" /> : null}
      </div>
      {!admin ? <p className="readonly-note">変更できるのは管理者のみです。</p> : null}
      <div className="paper" style={{ padding: "18px 22px 20px", marginBottom: 20, maxWidth: 760 }} data-testid="rfq-settings" {...a.formProps}>
        <div className="row" style={{ alignItems: "center", gap: 8 }}>
          {/* #218：空欄（既定）＝作る依頼の回答期限は空欄（紙に出ない）。日数を入れた会社だけ 送る日＋日数 */}
          <Field id="rqs_days" label={RFQ_MSG.replyDaysLabel} style={{ width: 220 }}>
            <NumInput kind="int" id="rqs_days" className="inp" value={f.replyDays} disabled={!admin} onChange={(v) => put({ replyDays: v })} data-testid="rfq-settings-days" />
          </Field>
          <span>日</span>
          {/* 「？」は欄の外（欄の中に置くと押すところが重なる＝settings-fit） */}
          <Help label={RFQ_MSG.replyDaysLabel} text={RFQ_MSG.replyDaysHelp} testId="help-rfq-days" />
        </div>
        <Field id="rqs_terms" label={RFQ_MSG.termsLabel}>
          <textarea id="rqs_terms" className="inp" rows={3} maxLength={1000} value={f.terms} disabled={!admin} onFocus={() => (last.current = "terms")} onChange={(e) => put({ terms: e.target.value })} data-testid="rfq-settings-terms" />
        </Field>
        <Field id="rqs_mailSubject" label={RFQ_MSG.mailSubjectLabel}>
          <input id="rqs_mailSubject" className="inp" maxLength={100} value={f.mailSubject} disabled={!admin} onFocus={() => (last.current = "mailSubject")} onChange={(e) => put({ mailSubject: e.target.value })} data-testid="rfq-settings-subject" />
        </Field>
        <Field id="rqs_mailBody" label={RFQ_MSG.mailBodyLabel}>
          <textarea id="rqs_mailBody" className="inp" rows={10} maxLength={2000} value={f.mailBody} disabled={!admin} onFocus={() => (last.current = "mailBody")} onChange={(e) => put({ mailBody: e.target.value })} data-testid="rfq-settings-body" />
        </Field>
        <div className="row" style={{ alignItems: "center", gap: 6, flexWrap: "wrap" }} role="group" aria-label={RFQ_MSG.mailFields}>
          <span className="hint">{RFQ_MSG.mailFields}</span>
          {RFQ_MAIL_FIELDS.map((x) => (
            <button key={x} type="button" className="btn sm" disabled={!admin} onMouseDown={(e) => e.preventDefault()} onClick={() => insert(x)} data-testid="rfq-field">
              {x}
            </button>
          ))}
        </div>
        <ErrorText>{err}</ErrorText>
        {admin && q.data.source === "tenant" ? (
          <div className="row" style={{ gap: 8, marginTop: 16 }}>
            <button type="button" className="btn" disabled={reset.isPending} onClick={() => reset.mutate()} data-testid="rfq-settings-reset">
              {RFQ_MSG.settingsReset}
            </button>
          </div>
        ) : null}
      </div>
      {back.dialog}
      {guard.dialog}
      {node}
    </>
  );
}
